/* eslint-disable arrow-body-style */

import { ThemedIcon } from "@src/common/ThemedIcon";
import { CLINICIAN_TYPE, LAYOUT_CONSTANTS } from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { useApplicationContext } from "@src/context/ApplicationContext";
import type { Provider } from "@src/interfaces/provider.interface";
import ClientLogsService from "@src/services/client-logs.service";
import { addToCalendar, createCalendarEventFromAppointment } from "@src/utils/calendar-utils";
import { getOrdinalSuffix } from "@src/utils/date-utils";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Card } from "@uhg-abyss/web/ui/Card";
import { Link } from "@uhg-abyss/web/ui/Link";
import { Text } from "@uhg-abyss/web/ui/Text";
import type { SyntheticEvent } from "react";

const FormSpacing = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "$web.semantic.spacing.scale.sm",
});

const AppointmentContentRow = styled("div", {
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  variants: {
    isMobile: {
      true: {
        flexDirection: "column",
        alignItems: "stretch",
        width: "100%",
        justifyContent: "center",
      },
    },
  },
});

const CalendarBrandPanel = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  width: "100%",
  maxWidth: "96px",
  backgroundColor: "$app.color.surface.container.primary",
  borderRadius: "8px 0 0 8px",
  paddingTop: "28px",
});

const AppointmentDetails = styled("div", {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  padding: "25px",
  gap: "1rem",
  textAlign: "center",
  variants: {
    isMobile: {
      true: {
        alignItems: "center",
        padding: "24px 20px 40px",
      },
    },
  },
});

const AppointmentDateRow = styled("div", {
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "flex-start",
  gap: "0.5rem",
  paddingBottom: "10px",
});

const AppointmentMetaRow = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.5rem",
  variants: {
    isMobile: {
      true: {
        alignItems: "center",
        gap: "0.25rem",
        width: "100%",
      },
    },
  },
});

const AppointmentTextColumn = styled("div", {
  display: "flex",
  flexDirection: "column",
  textAlign: "left",
  variants: {
    isMobile: {
      true: {
        alignItems: "flex-start",
        width: "100%",
      },
    },
  },
});

const DateTimeText = styled("div", {
  textAlign: "left",
  variants: {
    isMobile: {
      true: {
        overflowWrap: "anywhere",
      },
    },
  },
});

const AddToCalendarLink = styled(Link, {
  cursor: "pointer",
  color: "$app.color.text.link.rest.primary",
  fontSize: "18px",
  fontWeight: 700,
  variants: {
    isMobile: {
      true: {
        fontSize: "16px",
      },
    },
  },
});

const DetailLabelText = styled(Text, {
  fontWeight: 700,
});

const DetailBodyText = styled(Text, {
  fontSize: "16px",
});

export const AppointmentConfirmedCard = () => {
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const { data } = useApplicationContext();
  const providerDetails =
    typeof data.selectedProvider === "object" && data.selectedProvider !== null
      ? data.selectedProvider
      : ({} as Provider);
  const appointmentDetails = data.appointmentDetails;
  const isPrescriber = providerDetails.clinicianType === CLINICIAN_TYPE.PRESCRIBER;
  const sessionType = isPrescriber ? "medication management" : "therapy";

  const formatAppointmentDateTime = () => {
    if (!appointmentDetails?.apptDate || !appointmentDetails?.apptTime) {
      ClientLogsService.logError(
        data.userUuid || "unknown",
        "AppointmentConfirmedCard.formatAppointmentDateTime",
        "Appointment date or time not available",
        new Error("Missing appointment date or time"),
      );
      return "Appointment date and time not available";
    }

    try {
      const date = new Date(appointmentDetails.apptDate);
      const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
      const month = date.toLocaleDateString("en-US", { month: "long" });
      const day = date.getDate();
      const ordinalSuffix = getOrdinalSuffix(day);
      const time = appointmentDetails.apptTime;
      const timezone = appointmentDetails.userTimezone;

      return (
        <DateTimeText>
          {dayName}, {month} {day}
          {ordinalSuffix} at {time} {timezone}
        </DateTimeText>
      );
    } catch (error) {
      ClientLogsService.logError(
        data.userUuid || "unknown",
        "AppointmentConfirmedCard.formatAppointmentDateTime",
        "Error formatting appointment date and time",
        error instanceof Error ? error : new Error("Invalid date or time format"),
      );
      return "Invalid appointment date";
    }
  };

  const handleAddToCalendar = () => {
    try {
      const calendarEvent = createCalendarEventFromAppointment(
        appointmentDetails || {},
        providerDetails,
      );

      if (!calendarEvent) {
        ClientLogsService.logError(
          data.userUuid || "unknown",
          "AppointmentConfirmedCard.handleAddToCalendar",
          "Failed to create calendar event - missing appointment or provider details",
          new Error("calendarEvent is null"),
        );
        return;
      }

      addToCalendar(calendarEvent);
    } catch (error) {
      ClientLogsService.logError(
        data.userUuid || "unknown",
        "AppointmentConfirmedCard.handleAddToCalendar",
        "Error adding appointment to calendar",
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  };

  const handleFormSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  return (
    <Card
      className={isMobile ? "v2card-form-mobile-style" : ""}
      css={{
        width: "100%",
        maxWidth: LAYOUT_CONSTANTS.maxFormWidth,
        "abyss-card-root": {
          padding: "0 !important",
        },
      }}
    >
      <form id="appointment-confirmed" onSubmit={handleFormSubmit}>
        <FormSpacing>
          <AppointmentContentRow isMobile={isMobile}>
            {!isMobile && (
              <CalendarBrandPanel>
                <ThemedIcon
                  themeIconName="calendar_onetonedarkcircle"
                  themedImgProps={{ "aria-hidden": true }}
                  iconBrandProps={{
                    icon: "calendar",
                    size: 76,
                    variant: "onetonedarkcircle",
                    brand: "uhc",
                    style: { padding: "0px !important" },
                  }}
                />
              </CalendarBrandPanel>
            )}
            <AppointmentDetails isMobile={isMobile}>
              <AppointmentDateRow className="appointment-date">
                <DetailLabelText as="div" css={{ fontSize: isMobile ? "20px" : "22px" }}>
                  {formatAppointmentDateTime()}
                </DetailLabelText>
                <AddToCalendarLink
                  isMobile={isMobile}
                  id="obc-appointment-add-to-calendar-link"
                  data-testid="test-id-appointment-add-to-calendar-link"
                  data-obc-label="Appointment Add to Calendar Link"
                  onClick={() => handleAddToCalendar()}
                >
                  + Add to Calendar
                </AddToCalendarLink>
              </AppointmentDateRow>

              <AppointmentMetaRow isMobile={isMobile} className="appointment-therapist-details">
                <ThemedIcon
                  themeIconName="brain"
                  themedImgProps={{ "aria-hidden": true }}
                  iconBrandProps={{ size: "40", icon: "brain" }}
                />
                <AppointmentTextColumn isMobile={isMobile} className="appt-text">
                  <DetailLabelText>
                    {[providerDetails?.fullName, providerDetails?.providerType]
                      .filter(Boolean)
                      .join(", ")}
                  </DetailLabelText>
                  <DetailBodyText>{providerDetails.clinicianType}</DetailBodyText>
                </AppointmentTextColumn>
              </AppointmentMetaRow>

              {appointmentDetails?.careFormat === "in-person" &&
                appointmentDetails?.apptLocation && (
                  <AppointmentMetaRow isMobile={isMobile} className="appointment-place">
                    <ThemedIcon
                      themeIconName="location_pin"
                      themedImgProps={{ "aria-hidden": true }}
                      iconBrandProps={{ size: "40", icon: "location_pin" }}
                    />
                    <AppointmentTextColumn isMobile={isMobile} className="appt-text">
                      <DetailLabelText>
                        {appointmentDetails.apptLocation.clinic_name}
                      </DetailLabelText>
                      <DetailBodyText>
                        {[
                          appointmentDetails.apptLocation.address_line_1,
                          appointmentDetails.apptLocation.address_line_2,
                          appointmentDetails.apptLocation.city,
                          `${appointmentDetails.apptLocation.state} ${appointmentDetails.apptLocation.zip_code}`,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </DetailBodyText>
                    </AppointmentTextColumn>
                  </AppointmentMetaRow>
                )}

              <AppointmentMetaRow isMobile={isMobile} className="appointment-type">
                {appointmentDetails?.careFormat === "in-person" ? (
                  <ThemedIcon
                    themeIconName="conversation"
                    themedImgProps={{ "aria-hidden": true }}
                    iconBrandProps={{ size: "40", icon: "conversation" }}
                  />
                ) : (
                  <ThemedIcon
                    themeIconName="desktop"
                    themedImgProps={{ "aria-hidden": true }}
                    iconBrandProps={{ size: "40", icon: "desktop" }}
                  />
                )}
                <AppointmentTextColumn isMobile={isMobile} className="appt-text">
                  <DetailLabelText>
                    {appointmentDetails?.careFormat === "in-person"
                      ? `In-person ${sessionType} session`
                      : `Virtual ${sessionType} session`}
                  </DetailLabelText>
                  <DetailBodyText>{data.selectedProvider?.sessionDuration} minutes</DetailBodyText>
                </AppointmentTextColumn>
              </AppointmentMetaRow>
              {isPrescriber && (
                <AppointmentMetaRow isMobile={isMobile} className="appointment-disclaimer">
                  <DetailBodyText
                    css={{
                      fontSize: "14px",
                      color: "$app.color.text.secondary",
                      fontStyle: "italic",
                    }}
                  >
                    This appointment doesn&apos;t guarantee a prescription
                  </DetailBodyText>
                </AppointmentMetaRow>
              )}
            </AppointmentDetails>
          </AppointmentContentRow>
        </FormSpacing>
      </form>
    </Card>
  );
};
